/**
 * Giving per login (member split, multi-account).
 *
 * A member can sign in with several HOGC One logins. While hogc-one-api's
 * `member-split-multi-account` flag is on for the member, a login that has not
 * proved the member's personal id (partial NRIC + ddmm of the date of birth) sees
 * none of the member's giving records, though it can still give.
 *
 * Contract: hogc-one-api docs/member-split-giving-per-login.md.
 *
 * Feature detection: an older hogc-one-api has no /giving/login routes and sends
 * none of the new fields. Anything this module does not recognise is `null`, and
 * `null` means "behave exactly as before".
 */

export const GIVING_LOGIN_STATES = Object.freeze({
  VERIFIED: 'verified',
  VERIFICATION_REQUIRED: 'verification_required',
  NO_PERSONAL_ID: 'no_personal_id',
});

const KNOWN_STATES = new Set(Object.values(GIVING_LOGIN_STATES));

/** giving-api: a record route refused for a login that has not verified. */
export const GIVING_LOGIN_UNVERIFIED_CODE = 'GIVING_LOGIN_UNVERIFIED';

/** The codes the app branches on. `message` is display text and may be reworded. */
export const PERSONAL_ID_ERROR_CODES = Object.freeze({
  PERSONAL_ID_CLASH: 'PERSONAL_ID_CLASH',
  PERSONAL_ID_MISMATCH: 'PERSONAL_ID_MISMATCH',
  PERSONAL_ID_VERIFY_RATE_LIMITED: 'PERSONAL_ID_VERIFY_RATE_LIMITED',
  NO_PERSONAL_ID: 'NO_PERSONAL_ID',
  PERSONAL_ID_LOCKED: 'PERSONAL_ID_LOCKED',
  GIVING_LOGIN_UNKNOWN: 'GIVING_LOGIN_UNKNOWN',
});

export const VERIFY_PERSONAL_ID_PATH = '/giving/login/verify-personal-id';
export const GIVING_LOGIN_STATE_PATH = '/giving/login/state';

export const MY_GIVING_PATH = '/my-giving';

/**
 * MyGiving pages that only show the member's earlier giving: tithe history, the
 * building-fund page, the BF2026 pledge pages (keyed by the personal id) and the
 * recurring-gift removal. When giving-api refuses one of them for an unverified
 * login, the member goes to the landing page, which shows the personal-id check.
 * Pages that give (Give now, FAST, link a bank, Manage) stay where they are.
 */
const RECORD_PATH_PREFIXES = [
  '/my-giving/tithe-history/',
  '/my-giving/building-fund/',
  '/my-giving/building-fund-pledge',
  '/my-giving/remove-recurring',
];

export const isGivingRecordPath = path =>
  typeof path === 'string' && RECORD_PATH_PREFIXES.some(prefix => path.startsWith(prefix));

const codeOf = error => error?.response?.data?.code;

/** giving-api's 403 for a login in `verification_required`. */
export const isGivingLoginUnverified = error =>
  error?.response?.status === 403 && codeOf(error) === GIVING_LOGIN_UNVERIFIED_CODE;

/** 409 from the profile or stage-1 save: the personal id belongs to another member. */
export const isPersonalIdClash = error =>
  error?.response?.status === 409 && codeOf(error) === PERSONAL_ID_ERROR_CODES.PERSONAL_ID_CLASH;

/** 403 from the profile save: this login may not set the personal id; the rest saved. */
export const isPersonalIdLocked = error =>
  error?.response?.status === 403 && codeOf(error) === PERSONAL_ID_ERROR_CODES.PERSONAL_ID_LOCKED;

/** The 409 PERSONAL_ID_CLASH message, for an answer that carries none. */
export const PERSONAL_ID_CLASH_FALLBACK =
  'Oops, someone has the same personal ID! We’ll get this sorted out and be in touch.';

/** A profile save refused only the personal id: everything else was written. */
export const PERSONAL_ID_LOCKED_SAVED =
  'Changes saved. Your NRIC was not changed: verify your personal ID in MyGiving first.';

/**
 * The server's own clash message (kept word for word for older builds), without the
 * quotation marks it is wrapped in.
 */
export function personalIdClashMessage(error) {
  const message = error?.response?.data?.message;
  const text = typeof message === 'string' ? message.trim() : '';
  return (text || PERSONAL_ID_CLASH_FALLBACK).replace(/^[“"]+|[”"]+$/g, '').trim();
}

/**
 * `GET /giving/login/state` (or a verify answer) as `{ state, isPrimaryLogin }`, or
 * null for anything this build does not recognise.
 */
export function normalizeLoginState(data) {
  if (!data || !KNOWN_STATES.has(data.state)) return null;
  return {
    state: data.state,
    // Absent: treat as the primary, which is what every login was before.
    isPrimaryLogin: data.isPrimaryLogin !== false,
  };
}

const pad = n => String(n).padStart(2, '0');

/**
 * A calendar date as `yyyy-mm-dd`, the form the API takes. The date picker gives a
 * string already; a Date is read in the device's calendar, as the member picked it.
 */
function toIsoDate(value) {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
  }
  return typeof value === 'string' ? value.trim().slice(0, 10) : '';
}

/**
 * The body of `POST /giving/login/verify-personal-id`: exactly the two fields, the
 * partial NRIC trimmed. Throws rather than send an empty string, which the API's
 * validators reject.
 */
export function verifyPersonalIdPayload({ partialNric, dateOfBirth } = {}) {
  const nric = typeof partialNric === 'string' ? partialNric.trim() : '';
  const dob = toIsoDate(dateOfBirth);
  if (!nric || !dob) throw new Error('Partial NRIC and date of birth are both required');
  return { partialNric: nric, dateOfBirth: dob };
}

/** "about 15 minutes" / "about 24 hours", for a window length in seconds. */
export function retryWindowText(seconds) {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value <= 0) return '';
  const minutes = Math.ceil(value / 60);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  const hours = Math.ceil(minutes / 60);
  return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
}

export const VERIFY_ERROR_KINDS = Object.freeze({
  MISMATCH: 'mismatch',
  RATE_LIMITED: 'rate_limited',
  NO_PERSONAL_ID: 'no_personal_id',
  LOGIN_UNKNOWN: 'login_unknown',
  INVALID: 'invalid',
  UNAVAILABLE: 'unavailable',
  NETWORK: 'network',
  UNKNOWN: 'unknown',
});

const MESSAGES = {
  mismatch: 'Those details do not match the personal ID on your profile.',
  // One try a day per account (giving-api): past it, support confirms the details.
  rateLimited: 'This account has already tried to confirm the personal ID.',
  contactSupport: 'Please contact support and we will confirm it with you.',
  noPersonalId:
    'There is no personal ID on your record yet, so there is nothing to confirm. Contact support and we will help you set it up.',
  loginUnknown: 'We could not confirm which account this is. Please contact support.',
  invalid: 'Please check your date of birth and the last 4 characters of your NRIC.',
  unavailable: 'MyGiving is unavailable right now. Please try again shortly.',
  network: 'No internet connection. Please check your connection and try again.',
  unknown: 'Something went wrong. Please try again.',
};

/**
 * What the verify step shows for a failed `verify-personal-id`:
 * `{ kind, message, attemptsLeft, retryAfterSeconds, canRetry, offerSupport }`.
 * Never echoes what the member typed.
 */
export function mapVerifyPersonalIdError(error) {
  const response = error?.response;
  if (!response) {
    return {
      kind: VERIFY_ERROR_KINDS.NETWORK,
      message: error?.request ? MESSAGES.network : MESSAGES.unknown,
      attemptsLeft: null,
      retryAfterSeconds: null,
      canRetry: true,
      offerSupport: false,
    };
  }
  const { status, data = {} } = response;
  const base = { attemptsLeft: null, retryAfterSeconds: null };

  switch (data.code) {
    case PERSONAL_ID_ERROR_CODES.PERSONAL_ID_MISMATCH: {
      const attemptsLeft = Number.isFinite(data.attemptsLeft) ? data.attemptsLeft : null;
      const canRetry = attemptsLeft === null || attemptsLeft > 0;
      const tail =
        attemptsLeft === null
          ? ''
          : canRetry
            ? ` You have ${attemptsLeft} ${attemptsLeft === 1 ? 'attempt' : 'attempts'} left.`
            : ` ${MESSAGES.contactSupport}`;
      return {
        ...base,
        kind: VERIFY_ERROR_KINDS.MISMATCH,
        message: `${MESSAGES.mismatch}${tail}`,
        attemptsLeft,
        canRetry,
        offerSupport: true,
      };
    }
    case PERSONAL_ID_ERROR_CODES.PERSONAL_ID_VERIFY_RATE_LIMITED: {
      const retryAfterSeconds = Number.isFinite(data.retryAfterSeconds)
        ? data.retryAfterSeconds
        : null;
      return {
        ...base,
        kind: VERIFY_ERROR_KINDS.RATE_LIMITED,
        message: `${MESSAGES.rateLimited} ${MESSAGES.contactSupport}`,
        retryAfterSeconds,
        canRetry: false,
        offerSupport: true,
      };
    }
    case PERSONAL_ID_ERROR_CODES.NO_PERSONAL_ID:
      return {
        ...base,
        kind: VERIFY_ERROR_KINDS.NO_PERSONAL_ID,
        message: MESSAGES.noPersonalId,
        canRetry: false,
        offerSupport: true,
      };
    case PERSONAL_ID_ERROR_CODES.GIVING_LOGIN_UNKNOWN:
      return {
        ...base,
        kind: VERIFY_ERROR_KINDS.LOGIN_UNKNOWN,
        message: MESSAGES.loginUnknown,
        canRetry: false,
        offerSupport: true,
      };
    default:
      break;
  }

  if (status === 400) {
    return {
      ...base,
      kind: VERIFY_ERROR_KINDS.INVALID,
      message: MESSAGES.invalid,
      canRetry: true,
      offerSupport: false,
    };
  }
  if (status === 503) {
    return {
      ...base,
      kind: VERIFY_ERROR_KINDS.UNAVAILABLE,
      message: MESSAGES.unavailable,
      canRetry: true,
      offerSupport: false,
    };
  }
  return {
    ...base,
    kind: VERIFY_ERROR_KINDS.UNKNOWN,
    message: MESSAGES.unknown,
    canRetry: true,
    offerSupport: false,
  };
}
